// M13 Slice 5b — Section veya Question için override drawer.
// Section: { label?, sectionPrompt? }
// Question: { label?, aiContext? }
// Save: override objesi (boş alanları çıkar) → parent state'e yaz. Tüm alanlar
// boşsa override null gönder (key tamamen silinir).

import { useState } from "react";
import { X } from "lucide-react";
import type { QuestionOverride, SectionOverride } from "@mahir/shared";

interface BaseProps {
  targetLabel: string;
  targetCode: string;
  onClose: () => void;
}

interface SectionProps extends BaseProps {
  kind: "section";
  defaults: { label: string; prompt: string };
  current: SectionOverride | undefined;
  onSave: (override: SectionOverride | null) => void;
}

interface QuestionProps extends BaseProps {
  kind: "question";
  defaults: { label: string; aiContext: string };
  current: QuestionOverride | undefined;
  onSave: (override: QuestionOverride | null) => void;
}

type Props = SectionProps | QuestionProps;

export function OverrideDrawer(props: Props): JSX.Element {
  if (props.kind === "section") {
    return <SectionDrawer {...props} />;
  }
  return <QuestionDrawer {...props} />;
}

function SectionDrawer({
  targetLabel,
  targetCode,
  defaults,
  current,
  onClose,
  onSave,
}: SectionProps): JSX.Element {
  const [label, setLabel] = useState(current?.label ?? "");
  const [sectionPrompt, setSectionPrompt] = useState(current?.sectionPrompt ?? "");

  const handleSave = (): void => {
    const override: SectionOverride = {};
    if (label.trim() && label.trim() !== defaults.label) {
      override.label = label.trim();
    }
    if (sectionPrompt.trim() && sectionPrompt.trim() !== defaults.prompt) {
      override.sectionPrompt = sectionPrompt.trim();
    }
    if (Object.keys(override).length === 0) {
      onSave(null);
    } else {
      onSave(override);
    }
  };

  const handleClear = (): void => onSave(null);

  return (
    <DrawerShell
      title="Bölüm Override"
      subtitle={`${targetLabel} · ${targetCode}`}
      onClose={onClose}
      onSave={handleSave}
      onClear={current ? handleClear : null}
    >
      <Field
        label="Bölüm Başlığı (override)"
        hint={`Boş bırakırsanız parent'tan gelir: "${defaults.label}"`}
        maxLength={200}
      >
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={defaults.label}
          maxLength={200}
          style={inputStyle()}
        />
      </Field>
      <Field
        label="Section Prompt (Layer 3 override)"
        hint={`Boş bırakırsanız parent'tan gelir (parent uzunluğu: ${defaults.prompt.length.toString()})`}
        maxLength={5000}
      >
        <textarea
          value={sectionPrompt}
          onChange={(e) => setSectionPrompt(e.target.value)}
          placeholder={defaults.prompt || "(parent boş — varsayılan bölüm prompt'u)"}
          maxLength={5000}
          rows={8}
          style={{ ...inputStyle(), resize: "vertical", fontFamily: "var(--font-sans)" }}
        />
      </Field>
    </DrawerShell>
  );
}

function QuestionDrawer({
  targetLabel,
  targetCode,
  defaults,
  current,
  onClose,
  onSave,
}: QuestionProps): JSX.Element {
  const [label, setLabel] = useState(current?.label ?? "");
  const [aiContext, setAiContext] = useState(current?.aiContext ?? "");

  const handleSave = (): void => {
    const override: QuestionOverride = {};
    if (label.trim() && label.trim() !== defaults.label) {
      override.label = label.trim();
    }
    if (aiContext.trim() && aiContext.trim() !== defaults.aiContext) {
      override.aiContext = aiContext.trim();
    }
    if (Object.keys(override).length === 0) {
      onSave(null);
    } else {
      onSave(override);
    }
  };

  const handleClear = (): void => onSave(null);

  return (
    <DrawerShell
      title="Soru Override"
      subtitle={`${targetLabel} · ${targetCode}`}
      onClose={onClose}
      onSave={handleSave}
      onClear={current ? handleClear : null}
    >
      <Field
        label="Soru Etiketi (override)"
        hint={`Boş bırakırsanız parent'tan gelir: "${defaults.label}"`}
        maxLength={300}
      >
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={defaults.label}
          maxLength={300}
          style={inputStyle()}
        />
      </Field>
      <Field
        label="AI Bağlam Yönergesi (Layer 4 override)"
        hint={`Boş bırakırsanız parent'tan gelir (parent uzunluğu: ${defaults.aiContext.length.toString()})`}
        maxLength={3000}
      >
        <textarea
          value={aiContext}
          onChange={(e) => setAiContext(e.target.value)}
          placeholder={defaults.aiContext || "(parent boş — Layer 4 ipucu yok)"}
          maxLength={3000}
          rows={8}
          style={{ ...inputStyle(), resize: "vertical", fontFamily: "var(--font-sans)" }}
        />
      </Field>
    </DrawerShell>
  );
}

interface DrawerShellProps {
  title: string;
  subtitle: string;
  onClose: () => void;
  onSave: () => void;
  onClear: (() => void) | null;
  children: React.ReactNode;
}

function DrawerShell({
  title,
  subtitle,
  onClose,
  onSave,
  onClear,
  children,
}: DrawerShellProps): JSX.Element {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 100,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: "100vw",
          background: "var(--color-surface)",
          padding: 24,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.2rem",
                marginBottom: 4,
              }}
            >
              {title}
            </h3>
            <div
              style={{
                fontSize: "0.8rem",
                color: "var(--color-text-3)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--color-text-2)",
            }}
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1 }}>{children}</div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 12,
            borderTop: "1px solid var(--color-border)",
          }}
        >
          {onClear ? (
            <button
              onClick={onClear}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "1px solid var(--color-border)",
                background: "transparent",
                color: "var(--color-error)",
                cursor: "pointer",
                fontSize: "0.88rem",
              }}
            >
              Override'ı Kaldır
            </button>
          ) : (
            <div />
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "1px solid var(--color-border)",
                background: "transparent",
                cursor: "pointer",
                fontSize: "0.88rem",
              }}
            >
              İptal
            </button>
            <button
              onClick={onSave}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "none",
                background: "var(--color-primary)",
                color: "white",
                fontSize: "0.88rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Uygula
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  maxLength,
  children,
}: {
  label: string;
  hint?: string;
  maxLength?: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: "block",
          fontSize: "0.85rem",
          fontWeight: 600,
          marginBottom: 4,
          color: "var(--color-text)",
        }}
      >
        {label}
      </label>
      {children}
      {(hint ?? maxLength) !== undefined && (
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-text-3)",
            marginTop: 4,
            fontStyle: "italic",
          }}
        >
          {hint}
          {hint && maxLength ? " · " : ""}
          {maxLength ? `Max ${maxLength.toString()} karakter.` : ""}
        </div>
      )}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: "0.9rem",
    fontFamily: "var(--font-sans)",
  };
}
